import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

const CART_ITEM_ROW = 'tr[data-line-item-key]';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line, event);
    }

    // The row the shopper clicked, not refs.cartItemRows[line - 1]: rows can be hidden (a GC gift's priced excess)
    // and the cart can change under the page (GC gift sync), so positions drift.
    const clickedRow = event.target instanceof Element ? event.target.closest(CART_ITEM_ROW) : null;
    this.updateQuantity({
      line,
      key: clickedRow?.dataset.lineItemKey,
      quantity,
      action: 'change',
    });
    const lineItemRow = clickedRow ?? this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The triggering event.
   */
  onLineItemRemove(line, event) {
    const clickedRow = event?.target instanceof Element ? event.target.closest(CART_ITEM_ROW) : null;
    const cartItemRowToRemove = clickedRow ?? this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const eventRemoveButton =
      event?.target instanceof HTMLElement ? event.target.closest('.mini-cart__remove, .cart-items__remove') : null;
    const removeButton =
      eventRemoveButton instanceof HTMLButtonElement
        ? eventRemoveButton
        : cartItemRowToRemove.querySelector('.mini-cart__remove, .cart-items__remove');

    if (removeButton instanceof HTMLButtonElement) {
      this.#setRemoveButtonLoadingState(removeButton, true);
    }

    this.updateQuantity({
      line,
      key: cartItemRowToRemove.dataset.lineItemKey,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.key] - The line item key; sent instead of the line position when known.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // By line key when known: a position can point at another line once the cart changed under the page (another
    // tab, GC gift sync adding / splitting / removing a gift line) — then a +/- on one item changed or removed another.
    const body = JSON.stringify({
      ...(key ? { id: key } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        // 400 "no valid id or line parameter": the line changed under the page (its key is gone). Nothing was changed;
        // re-render so the rows and quantities match the real cart again.
        if (!parsedResponseText.sections) {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
        this.#refreshDeliveryFees();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#setAllRemoveButtonsLoadingState(false);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const sectionHtml = event.detail.resource.sections?.[this.sectionId];
      if (sectionHtml) {
        morphSection(this.sectionId, this.#preserveDiscountSheetTab(sectionHtml));
      } else {
        // Wait for the morph, otherwise it overwrites the fee computed by the refresh.
        sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => this.#refreshDeliveryFees());
        return;
      }
      this.#refreshDeliveryFees();
      return;
    }
    if (event.target === this) return;
    if (event.target instanceof CartItemsComponent) {
      const sourceDrawer = event.target.closest('cart-drawer-component');
      const currentDrawer = this.closest('cart-drawer-component');
      if (sourceDrawer && sourceDrawer === currentDrawer) {
        return;
      }
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#refreshDeliveryFees();
    } else {
      // Wait for the morph, otherwise it overwrites the fee computed by the refresh.
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => this.#refreshDeliveryFees());
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Sets the loading state for a single remove button.
   * @param {HTMLButtonElement} button
   * @param {boolean} isLoading
   */
  #setRemoveButtonLoadingState(button, isLoading) {
    button.classList.toggle('is-loading', isLoading);
    button.setAttribute('aria-busy', String(isLoading));
  }

  /**
   * Sets loading state for all remove buttons.
   * @param {boolean} isLoading
   */
  #setAllRemoveButtonsLoadingState(isLoading) {
    for (const button of this.querySelectorAll('.mini-cart__remove, .cart-items__remove')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      this.#setRemoveButtonLoadingState(button, isLoading);
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Refreshes all delivery fee widgets after cart changes.
   */
  #refreshDeliveryFees() {
    requestAnimationFrame(() => {
      for (const deliveryFees of document.querySelectorAll('cart-delivery-fees')) {
        if (typeof deliveryFees.refresh !== 'function') continue;
        deliveryFees.refresh();
      }
    });
  }

  /**
   * Toggles the cart discount breakdown visibility.
   * @param {MouseEvent} event
   */
  toggleDiscountBreakdown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[aria-controls="cart-summary-breakdown"], .mini-cart__discount-trigger');
    if (!(trigger instanceof HTMLButtonElement)) return;

    const panel = trigger.closest('.cart-summary-panel, .mini-cart');
    if (!(panel instanceof HTMLElement)) return;

    const breakdown =
      panel.querySelector('#cart-summary-breakdown') ||
      panel.querySelector('.cart-summary__breakdown, .mini-cart__breakdown');

    if (!(breakdown instanceof HTMLElement)) return;

    breakdown.classList.toggle('hidden');
    trigger.setAttribute('aria-expanded', String(!breakdown.classList.contains('hidden')));
  };

  /**
   * Preserves the active cart discount tab when the cart section is morphed.
   * @param {string} markup
   * @returns {string}
   */
  #preserveDiscountSheetTab(markup) {
    const sheet = document.querySelector('cart-discount-sheet');
    const activeTab =
      sheet?.dataset.activeTab ||
      sheet?.querySelector('[data-discount-tab][aria-selected="true"]')?.dataset.discountTab;

    if (!activeTab) return markup;

    return markup.replace(
      /(<cart-discount-sheet\b[^>]*)(>)/,
      (match, start, end) => {
        const activeTabAttr = ` data-active-tab="${activeTab}"`;
        if (start.includes('data-active-tab=')) return match;
        return `${start}${activeTabAttr}${end}`;
      }
    );
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
